import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { minMaxAxisDomain } from '@/components/Charts/utils';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePathSet } from '@/hooks/usePathSet';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';
import { printYear } from '@/utils/formatting/date';

import { NoData } from '../../../components/NoData';
import { Tile, TileCapsuleContent, TileCta, TileTitle } from './Tile';

export const SocialTile = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();

  const plotGroupId = usePlotGroupId();
  const { pathname, search } = useLocation();
  const currentPath = `${pathname}${search}`;

  const socialLifeExpectancyHistoricGraphDataFact = IndicatorName.social_life_expectancy_historic_graph;
  const { [socialLifeExpectancyHistoricGraphDataFact]: socialLifeExpectancyHistoricGraphData } =
    useOrganizationIndicatorGraphFact<SocialLifeExpectancyHistoricGraphData[]>(
      socialLifeExpectancyHistoricGraphDataFact,
    );
  const socialLifeExpectancyHistoricGraphDataFormatted = (socialLifeExpectancyHistoricGraphData?.value ?? [])
    // @ts-ignore typescript does not allow arithmetic operations between date objects
    .sort((pointA, pointB) => new Date(pointA.date) - new Date(pointB.date))
    .map((d) => ({ ...d, date: new Date(d.date).getTime() }));

  // TODO: shift to call timeseries graphfact with date filter (blocked by BE API support)
  const socialLatestAnalysis = socialLifeExpectancyHistoricGraphDataFormatted.at(-1);

  let socialLatestAnalysisDisplay = `${useDisplayNumber(socialLatestAnalysis?.value ?? 0)} ${t('shared.ncaDetail.details.social.labels.years')}`;
  socialLatestAnalysisDisplay = !isNullish(socialLatestAnalysis?.value) ? socialLatestAnalysisDisplay : '-';

  const chartData = socialLifeExpectancyHistoricGraphDataFormatted;

  const noChartData = !chartData?.length;

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Tile
      chart={<Chart chartData={chartData} />}
      title={<TileTitle>{t('global.analysis.social')}</TileTitle>}
      capsuleContent={<TileCapsuleContent className='lowercase'>{socialLatestAnalysisDisplay}</TileCapsuleContent>}
      cta={
        <TileCta to={buildPath(pathSet.socialDetails, { queryParams: { plotGroupId } })} state={{ from: currentPath }}>
          {t('global.analysis.viewDetails')}
        </TileCta>
      }
    />
  );
};

type SocialLifeExpectancyHistoricGraphData = {
  date: number;
  name: 'social_life_expectancy';
  value: number;
  unit: 'years';
};

const Chart = ({ chartData }: { chartData: SocialLifeExpectancyHistoricGraphData[] }) => {
  const yearsSubset = chartData.map((d) => d.date).filter((_, i) => i !== chartData.length - 1);

  return (
    <ResponsiveContainer width='100%' height={80}>
      <AreaChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
      >
        <defs>
          <linearGradient id='lifeExpectancy' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor='#BA91BB' stopOpacity={0.3} />
            <stop offset='100%' stopColor='#BA91BB' stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          isAnimationActive={false}
          type='monotone'
          dataKey='value'
          stroke='#BA91BB'
          strokeWidth={2}
          fill='url(#lifeExpectancy)'
          fillOpacity={1}
        />
        <XAxis
          dataKey='date'
          scale='time'
          type='number'
          domain={['dataMin', 'dataMax']}
          ticks={yearsSubset}
          tickFormatter={printYear}
          axisLine={false}
          tickLine={false}
        />
        <YAxis hide dataKey='value' type='number' scale='sequential' width={0} domain={minMaxAxisDomain(0, 1.2)} />
      </AreaChart>
    </ResponsiveContainer>
  );
};
